"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@/components/icons";

/**
 * Desktop showcase feature carousel — shows ONE feature slide at a time with
 * prev/next chevrons + clickable dots. Loops infinitely in both directions
 * (no dead-end at the ends).
 *
 * ## Seamless infinite loop (clone technique)
 *
 * A plain `translateX(-index*100%)` track can't wrap without rewinding across
 * every slide. So the track renders `[clone(last), ...slides, clone(first)]`
 * and tracks a display position `pos` (real slides live at pos 1..n). Stepping
 * past the last real slide slides onto the leading clone of the first; once
 * that transition ends we snap `pos` back to the real first slide WITHOUT a
 * transition — invisible, because the clone is identical. Same trick mirrored
 * at the start. Result: next/prev always continue in the pressed direction and
 * never stop.
 *
 * ## Height-adaptive
 *
 * Slides differ in height (the muscle-map slide is much taller than the ghost
 * card). The viewport height tracks the ACTIVE slide — set synchronously on
 * navigation + kept in sync by a ResizeObserver (the muscle map's SVG loads in
 * late) — so shorter slides don't float in the tallest slide's dead space.
 *
 * Slide CONTENT is built + passed in by the server `DesktopShowcase` (copy
 * stays SSR'd); this client component only owns the carousel mechanics.
 * Off-screen display items are `inert` (out of the tab order + a11y tree).
 */
export function FeatureCarousel({ slides }: { slides: ReactNode[] }) {
  const count = slides.length;
  // Display track = [clone(last), ...real, clone(first)]. Real slide i sits at
  // display position i+1; positions 0 and count+1 are the wrap clones.
  const displayItems =
    count > 1 ? [slides[count - 1], ...slides, slides[0]] : slides;

  const [pos, setPos] = useState(count > 1 ? 1 : 0);
  const [animate, setAnimate] = useState(true);
  const [height, setHeight] = useState<number | undefined>(undefined);
  const refs = useRef<(HTMLDivElement | null)[]>([]);

  // Real (logical) slide index for the dots / a11y, mapping the wrap clones
  // back to the real slide they duplicate.
  const realIndex =
    count <= 1 ? 0 : pos === 0 ? count - 1 : pos === count + 1 ? 0 : pos - 1;

  function moveTo(nextPos: number) {
    // Ignore taps while parked on a wrap clone — a silent snap-back is pending,
    // and this keeps pos within bounds.
    if (pos === 0 || pos === count + 1) return;
    const el = refs.current[nextPos];
    if (el) setHeight(el.offsetHeight); // sync so height animates with the slide
    setAnimate(true);
    setPos(nextPos);
  }

  // After a wrap transition lands on a clone, snap (no transition) to the real
  // twin. The clone is identical, so the jump is invisible.
  function onTransitionEnd(e: React.TransitionEvent<HTMLDivElement>) {
    if (e.target !== e.currentTarget || e.propertyName !== "transform") return;
    if (pos === count + 1) {
      setAnimate(false);
      setPos(1);
    } else if (pos === 0) {
      setAnimate(false);
      setPos(count);
    }
  }

  // Re-enable the transition the frame after a silent snap-back commits, so the
  // next nav animates (and the snap itself never does).
  useEffect(() => {
    if (animate) return;
    const id = requestAnimationFrame(() => setAnimate(true));
    return () => cancelAnimationFrame(id);
  }, [animate]);

  // Keep the viewport sized to the active slide as it changes height after
  // mount (the muscle map's SVG loads in late). observe() fires its first
  // callback async, so this never sets state synchronously in-effect.
  useEffect(() => {
    const el = refs.current[pos];
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setHeight(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, [pos]);

  if (count <= 1) {
    return <div>{slides[0] ?? null}</div>;
  }

  return (
    <div
      role="group"
      aria-roledescription="carousel"
      aria-label="Feature highlights"
    >
      <div
        className="overflow-hidden transition-[height] duration-300 ease-out"
        style={{ height }}
      >
        <div
          onTransitionEnd={onTransitionEnd}
          className={
            "flex items-start " +
            (animate ? "transition-transform duration-300 ease-out" : "")
          }
          style={{ transform: `translateX(-${pos * 100}%)` }}
        >
          {displayItems.map((slide, i) => {
            // Map the wrap clones (display 0 / count+1) back to the real slide
            // they duplicate, so each item's label reflects its own position.
            const itemReal =
              i === 0 ? count - 1 : i === count + 1 ? 0 : i - 1;
            return (
              <div
                key={i}
                ref={(el) => {
                  refs.current[i] = el;
                }}
                className="w-full shrink-0"
                role="group"
                aria-roledescription="slide"
                aria-label={`${itemReal + 1} of ${count}`}
                inert={i !== pos}
              >
                {slide}
              </div>
            );
          })}
        </div>
      </div>

      {/* Controls — prev chevron · dots · next chevron (never disabled: loops) */}
      <div className="mt-8 flex items-center justify-center gap-4">
        <button
          type="button"
          onClick={() => moveTo(pos - 1)}
          aria-label="Previous feature"
          className="h-9 w-9 rounded-full border border-border text-fg-muted flex items-center justify-center transition-colors hover:text-fg hover:border-fg-dim"
        >
          <ChevronLeftIcon className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-2">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => moveTo(i + 1)}
              aria-label={`Go to feature ${i + 1}`}
              aria-current={i === realIndex}
              className={
                "h-2 rounded-full transition-all " +
                (i === realIndex
                  ? "w-6 bg-brand"
                  : "w-2 bg-border hover:bg-fg-dim")
              }
            />
          ))}
        </div>

        <button
          type="button"
          onClick={() => moveTo(pos + 1)}
          aria-label="Next feature"
          className="h-9 w-9 rounded-full border border-border text-fg-muted flex items-center justify-center transition-colors hover:text-fg hover:border-fg-dim"
        >
          <ChevronRightIcon className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
